import { useState } from "react";

import {
  formatDeclination,
  latitudeFromMeridianAltitude,
} from "@nav-games/astro";

/// Meridian-altitude calculator — the "noon Sun" shortcut. At the
/// moment a body crosses your meridian (its LHA = 0), latitude drops
/// straight out of:
///
///   Lat = Dec + (90° − Ho)   when the body bears south of you
///   Lat = Dec − (90° − Ho)   when the body bears north of you

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function Field({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
      <span className="small muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={0.1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function MeridianAltitudeCalculator() {
  const [ho, setHo] = useState("59.0");
  const [dec, setDec] = useState("20.0");
  const [bearing, setBearing] = useState<"N" | "S">("S");

  const valid =
    ho.trim() !== "" &&
    dec.trim() !== "" &&
    Number.isFinite(Number(ho)) &&
    Number.isFinite(Number(dec));

  const lat = valid
    ? latitudeFromMeridianAltitude({
        hoDeg: Number(ho),
        decDeg: Number(dec),
        bodyBearing: bearing,
      })
    : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Meridian altitude → latitude</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Take a sight as the body crosses your meridian (the moment the
        altitude stops increasing). For UK navigators the Sun usually
        bears south at transit; if you're in the tropics south of the
        Sun's GP, it bears north.
      </p>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <Field label="Ho (°)" value={ho} onChange={setHo} hint="corrected observed altitude" />
        <Field
          label="Declination (°)"
          value={dec}
          onChange={setDec}
          hint="N positive · S negative"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div className="small muted" style={{ marginBottom: 4 }}>
          At transit, the body bore:
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {(["S", "N"] as const).map((b) => (
            <button
              key={b}
              type="button"
              className={bearing === b ? "" : "secondary"}
              onClick={() => setBearing(b)}
              style={{ flex: 1 }}
              aria-pressed={bearing === b}
            >
              {b === "S" ? "South of me" : "North of me"}
            </button>
          ))}
        </div>
      </div>

      {lat !== null ? (
        <>
          <div
            style={{
              textAlign: "center",
              padding: "10px 4px",
              marginBottom: 12,
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--accent)",
              background: "var(--accent-soft)",
            }}
          >
            <div className="small muted">Latitude</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {formatDeclination(lat)}
            </div>
          </div>

          <div
            className="small"
            style={{
              padding: 12,
              background: "var(--bg-elev)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
            {`Working
─────────────────────────────────────────────
ZD (zenith distance) = 90° − Ho
                     = 90° − ${Number(ho).toFixed(1)}°
                     = ${(90 - Number(ho)).toFixed(1)}°

Body bears ${bearing === "S" ? "South" : "North"} →
  Lat = Dec ${bearing === "S" ? "+" : "−"} ZD
      = ${Number(dec).toFixed(1)}° ${bearing === "S" ? "+" : "−"} ${(90 - Number(ho)).toFixed(1)}°
      = ${lat.toFixed(1)}°   (${formatDeclination(lat)})`}
          </div>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter Ho and Dec to compute your latitude.
        </p>
      )}
    </div>
  );
}
